import type { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";

/**
 * Customer.io Data Pipelines (CDP) browser capture for the Dialer waitlist
 * (GLE-8116).
 *
 * The browser write key is public by design and read from
 * NEXT_PUBLIC_CIO_CDP_WRITE_KEY. It comes from a JavaScript source in the
 * Glencoco workspace (133165); until that source is provisioned and the env
 * var is set, capture is skipped so the page still works.
 *
 * Region: US default (https://cdp.customer.io). Set the EU CDN URL in the
 * AnalyticsBrowser.load options if the workspace ever moves to the EU data
 * center.
 */

export interface WaitlistLead {
  name: string;
  email: string;
  company: string;
  phone: string;
  currentDialer: string;
  repsDialing: string;
  callVolumePerRep: string;
  crmSep: string;
}

// The SDK is imported dynamically and only on the client: customerio-gist-web
// (in-app messaging) touches HTMLElement at module load, which crashes Next's
// server-side page-data collection. A type-only import keeps the server bundle
// clean. The loaded client is wrapped in a plain object so this thenable value
// is never returned directly from an async function — otherwise JS unwraps it
// and we lose the buffered identify/track methods (Customer.io web-integration
// guide). The promise is cached so the SDK loads once.
let clientPromise: Promise<{ analytics: AnalyticsBrowser } | null> | null =
  null;

const getClient = (): Promise<{ analytics: AnalyticsBrowser } | null> => {
  if (!clientPromise) {
    clientPromise = (async () => {
      const writeKey = process.env.NEXT_PUBLIC_CIO_CDP_WRITE_KEY;

      if (!writeKey) {
        return null;
      }

      const { AnalyticsBrowser } =
        await import("@customerio/cdp-analytics-browser");

      return { analytics: AnalyticsBrowser.load({ writeKey }) };
    })();
  }

  return clientPromise;
};

/**
 * Sends a waitlist signup to Customer.io: identifies the person by email
 * (with name, company and the qualification answers as traits) and fires a
 * "Dialer Waitlist Signup" event that marketing can trigger the discount
 * campaign from.
 *
 * Returns true when the events were queued, false when capture was skipped
 * because no write key is configured. Throws only on an unexpected client
 * failure, so the caller can surface a retry.
 */
export const captureWaitlistSignup = async ({
  name,
  email,
  company,
  phone,
  currentDialer,
  repsDialing,
  callVolumePerRep,
  crmSep,
}: WaitlistLead): Promise<boolean> => {
  const loaded = await getClient();

  if (!loaded) {
    // eslint-disable-next-line no-console
    console.warn(
      "[waitlist] NEXT_PUBLIC_CIO_CDP_WRITE_KEY is not set — skipping Customer.io capture"
    );

    return false;
  }

  // The workspace personalizes emails with first_name / fullname, so mirror the
  // single name field into both (first_name = first token) alongside raw name.
  const traits: Record<string, string> = {
    email,
    name,
    fullname: name,
    first_name: name.split(/\s+/)[0],
    company,
    phone,
    current_dialer: currentDialer,
    reps_dialing: repsDialing,
    call_volume_per_rep: callVolumePerRep,
    crm_sep: crmSep,
  };

  await loaded.analytics.identify(email, traits);
  await loaded.analytics.track("Dialer Waitlist Signup", traits);

  return true;
};
